import { db } from "./firebase";
import { collection, doc, getDocs, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { integrationToFirestore, integrationFromFirestore } from "../models/integration";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

// ──────────────────────────────────────────────────────────────────────────────
// KILL-SWITCH — envio de WhatsApp pelo PAINEL.
// REATIVADO (false): o whats de vendas foi desligado e a automação voltou para o
// NeoApp, então o consultor pode enviar manualmente pelo painel de novo.
// (Coloque `true` para bloquear o envio manual, ex.: se reativar o bot whats.)
// ──────────────────────────────────────────────────────────────────────────────
const PANEL_EVOLUTION_SEND_DISABLED = false;
const PANEL_DISABLED_REASON =
    "Envio de WhatsApp pelo painel está temporariamente desativado — as mensagens aos clientes estão centralizadas no bot (whats).";

/**
 * IntegrationService
 * Centraliza as operações de banco de dados para Integrações externas.
 * Coleção: tenants/{idTenant}/integrations
 */
const IntegrationService = {
    /**
     * Retorna a lista de todas as integrações configuradas para o tenant.
     * Ex: tenants/{idTenant}/integrations/{provider}
     */
    getAll: async (idTenant) => {
        if (!idTenant) throw new Error("ID do tenant não fornecido.");

        const integrationsRef = collection(db, "tenants", idTenant, COLLECTIONS.INTEGRATIONS);
        const snapshot = await getDocs(integrationsRef);

        return snapshot.docs.map(integrationFromFirestore).filter(Boolean);
    },

    /**
     * Busca as configurações de uma integração específica.
     */
    getByProvider: async (idTenant, provider) => {
        if (!idTenant || !provider) {
            throw new Error("ID do tenant e nome do provider são obrigatórios.");
        }

        const docRef = doc(db, "tenants", idTenant, "integrations", provider);
        const snapshot = await getDoc(docRef);

        return integrationFromFirestore(snapshot);
    },

    /**
     * Salva ou atualiza a configuração de uma integração.
     */
    save: async (integrationData, idTenant, authUser) => {
        if (!integrationData.provider) {
            throw new Error("Provider da integração não informado.");
        }

        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const docId = integrationData.id || integrationData.provider;
        const docRef = doc(db, "tenants", idTenant, "integrations", docId);

        const payload = integrationToFirestore(integrationData, { id: idTenant }, authUser);

        await setDoc(docRef, payload, { merge: true });

        // Auditoria
        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.INTEGRATIONS,
            action: integrationData.id ? AUDIT_ACTIONS.UPDATE : AUDIT_ACTIONS.CREATE,
            entityId: docId,
            entityName: integrationData.name || integrationData.provider,
            description: `${integrationData.id ? 'Atualizou' : 'Configurou'} a integração "${integrationData.name || integrationData.provider}"`
        });

        return { id: docId, ...payload };
    },

    /**
     * Exclui (ou limpa) as configurações de uma integração.
     */
    delete: async (docId, idTenant, authUser, integrationName) => {
        if (!idTenant || !docId) {
            throw new Error("ID do tenant e ID da integração são obrigatórios para exclusão.");
        }

        const docRef = doc(db, "tenants", idTenant, "integrations", docId);
        await deleteDoc(docRef);

        // Auditoria
        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.INTEGRATIONS,
            action: AUDIT_ACTIONS.DELETE,
            entityId: docId,
            entityName: integrationName || docId,
            description: `Excluiu a integração "${integrationName || docId}"`
        });
    },

    /**
     * Extrai uma mensagem de erro segura da resposta da API.
     */
    extractErrorMessage: (data, fallback = "Erro desconhecido.") => {
        if (typeof data?.message === "string") return data.message;
        if (typeof data?.error?.message === "string") return data.error.message;
        if (typeof data?.response?.message === "string") return data.response.message;
        if (Array.isArray(data?.response?.message)) {
            const first = data.response.message[0];
            if (first?.exists === false) return `Número não encontrado no WhatsApp.`;
            return data.response.message.flat().join(", ");
        }
        return fallback;
    },

    /**
     * Testa a conexão/envio com um provider.
     */
    testConnection: async (providerId, config, testInput) => {
        if (providerId === "openai") {
            try {
                const res = await fetch("https://api.openai.com/v1/chat/completions", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${config.apiKey}`,
                    },
                    body: JSON.stringify({
                        model: config.defaultModel || "gpt-4o-mini",
                        messages: [
                            { role: "system", content: config.customPrompt || config.systemPrompt || "Você é um assistente prestativo." },
                            { role: "user", content: testInput || "Diga uma curiosidade." },
                        ],
                        max_tokens: 200,
                    }),
                });
                const data = await res.json();
                
                const textRes = data.choices?.[0]?.message?.content;
                return { success: !data.error, message: data.error ? data.error.message : (textRes || "Conexão OK, mas não retornou texto!") };
            } catch (err) {
                return { success: false, message: err.message || "Erro desconhecido" };
            }
        } else if (providerId === "gemini") {
            try {
                const model = config.defaultModel || "gemini-1.5-flash";
                const body = { contents: [{ parts: [{ text: testInput || "Diga uma curiosidade." }] }] };
                const sysPrompt = config.customPrompt || config.systemPrompt;
                if (sysPrompt) body.systemInstruction = { parts: [{ text: sysPrompt }] };

                const res = await fetch(
                    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${config.apiKey}`,
                    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
                );
                const data = await res.json();
                
                const textRes = data.candidates?.[0]?.content?.parts?.[0]?.text;
                return { success: !data.error, message: data.error ? data.error.message : (textRes || "Conexão OK, mas não retornou texto!") };
            } catch (err) {
                return { success: false, message: err.message || "Erro desconhecido" };
            }
        } else if (providerId === "evolution") {
            const baseUrl = (config.apiUrl || "").replace(/\/$/, "");
            const instance = config.instanceName;

            const stateRes = await fetch(`${baseUrl}/instance/connectionState/${instance}`, {
                method: "GET",
                headers: { apikey: config.apiKey },
            });
            const stateData = await stateRes.json();

            if (!stateRes.ok) {
                return { success: false, message: IntegrationService.extractErrorMessage(stateData, "Erro ao verificar instância.") };
            }

            const state = stateData?.instance?.state || stateData?.state || "desconhecido";
            if (state !== "open") {
                return { success: false, message: `Instância "${instance}" não conectada (estado: "${state}"). Escaneie o QR Code.` };
            }

            if (!testInput || testInput.replace(/\D/g, "").length < 10) {
                return { success: true, message: `✅ Conectado! Instância "${instance}" está online.\n\nDigite um número para enviar uma mensagem de teste.` };
            }

            const number = testInput.replace(/\D/g, "");
            if (PANEL_EVOLUTION_SEND_DISABLED) {
                return { success: true, message: `✅ Instância "${instance}" conectada e online.\n\n(Envio de teste desativado — mensagens aos clientes estão centralizadas no bot/whats.)` };
            }
            const msgRes = await fetch(`${baseUrl}/message/sendText/${instance}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", apikey: config.apiKey },
                body: JSON.stringify({ number, text: "✅ Mensagem de teste do NeoApp — integração funcionando!" }),
            });
            const msgData = await msgRes.json();

            return msgRes.ok
                ? { success: true, message: `✅ Mensagem enviada com sucesso para ${number}!` }
                : { success: false, message: IntegrationService.extractErrorMessage(msgData, "Erro ao enviar mensagem.") };
        }
    },

    /**
     * Analisa uma imagem e extrai um JSON estruturado (usando OpenAI ou Gemini).
     */
    parseImageWithAI: async (providerId, config, systemPrompt, base64Data, mimeType) => {
        try {
            if (providerId === "openai") {
                const res = await fetch("https://api.openai.com/v1/chat/completions", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${config.apiKey}`,
                    },
                    body: JSON.stringify({
                        model: config.defaultModel && config.defaultModel.includes('vision') ? config.defaultModel : "gpt-4o",
                        messages: [
                            { role: "system", content: systemPrompt },
                            { 
                                role: "user", 
                                content: (mimeType.includes('text') || mimeType.includes('csv') || mimeType.includes('xml') || mimeType.includes('ofx') || mimeType.includes('vnd')) ? [
                                    { type: "text", text: `Extraia as despesas do texto deste documento seguindo precisamente as instruções e retornando apenas o array JSON. Documento/Extrato:\n\n${base64Data}` }
                                ] : [
                                    { type: "text", text: "Extraia as despesas desta imagem seguindo precisamente as instruções e retornando apenas o array JSON." },
                                    { type: "image_url", image_url: { url: `data:${mimeType};base64,${base64Data}` } }
                                ]
                            }
                        ],
                        max_tokens: 1500,
                    }),
                });
                const data = await res.json();
                if (data.error) return { success: false, message: data.error.message };
                
                let textContent = data.choices?.[0]?.message?.content || "";
                // Limpeza básica se vier com crase
                textContent = textContent.replace(/```json/g, '').replace(/```/g, '').trim();
                
                try {
                    const parsed = JSON.parse(textContent);
                    return { success: true, data: parsed };
                } catch(e) {
                    return { success: false, message: "A IA não retornou um JSON válido." };
                }
            } else if (providerId === "gemini") {
                const model = config.defaultModel || "gemini-2.5-flash";
                
                const isText = mimeType.includes('text') || mimeType.includes('csv') || mimeType.includes('xml') || mimeType.includes('ofx') || mimeType.includes('vnd');
                const bodyParts = isText 
                    ? [ { text: `Extraia as despesas deste extrato textual retornando apenas o array JSON e sem formatadores. O CONTEÚDO DO DOCUMENTO:\n\n${base64Data}` } ]
                    : [ { text: "Extraia as despesas desta imagem retornando apenas o array JSON e sem formatadores." }, { inlineData: { data: base64Data, mimeType } } ];

                const body = {  
                    contents: [{ parts: bodyParts }],
                    generationConfig: {
                        responseMimeType: "application/json"
                    }
                };
                if (systemPrompt) body.systemInstruction = { parts: [{ text: systemPrompt }] };

                const res = await fetch(
                    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${config.apiKey}`,
                    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
                );
                const data = await res.json();
                
                if (data.error) return { success: false, message: data.error.message };
                
                let textContent = data.candidates?.[0]?.content?.parts?.[0]?.text;
                textContent = textContent.replace(/```json/g, '').replace(/```/g, '').trim();
                
                try {
                    const parsed = JSON.parse(textContent);
                    return { success: true, data: parsed };
                } catch(e) {
                    return { success: false, message: "A IA não retornou um JSON válido." };
                }
            }
            return { success: false, message: "Provedor não suportado para leitura de imagem." };
        } catch (error) {
            return { success: false, message: error.message };
        }
    },

    /**
     * Verifica o status da Evolution API.
     */
    checkEvolutionStatus: async (config) => {
        const baseUrl = config.apiUrl.replace(/\/$/, "");
        const res = await fetch(`${baseUrl}/instance/connectionState/${config.instanceName}`, {
            method: "GET",
            headers: { apikey: config.apiKey },
        });
        const data = await res.json();
        return { ok: res.ok, state: data?.instance?.state || data?.state || "error" };
    },

    /**
     * Envia uma mensagem via Evolution API
     * Formata o número (adiciona 55 se Brasil, remove não numéricos)
     */
    sendEvolutionMessage: async (config, phoneNumber, text) => {
        if (PANEL_EVOLUTION_SEND_DISABLED) {
            console.warn("[IntegrationService] sendEvolutionMessage bloqueado:", PANEL_DISABLED_REASON);
            return { success: false, reason: PANEL_DISABLED_REASON, disabled: true };
        }
        const baseUrl = (config.apiUrl || "").replace(/\/$/, "");
        const instance = config.instanceName;

        // Formatar número de telefone
        let phoneNum = (phoneNumber || "").replace(/\D/g, "");
        if (phoneNum.startsWith("0")) phoneNum = phoneNum.substring(1);
        if (phoneNum && !phoneNum.startsWith("55")) phoneNum = "55" + phoneNum;

        if (!phoneNum || phoneNum.length < 10) {
            return { success: false, reason: "invalid_phone" };
        }

        const msgRes = await fetch(`${baseUrl}/message/sendText/${instance}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", apikey: config.apiKey },
            body: JSON.stringify({ number: phoneNum, text }),
        });

        if (!msgRes.ok) {
            const msgData = await msgRes.json();
            return { success: false, reason: IntegrationService.extractErrorMessage(msgData, "Erro ao enviar mensagem.") };
        }

        return { success: true };
    },

    /**
     * Envia uma imagem com legenda via Evolution API
     * @param {Object} config - { apiUrl, apiKey, instanceName }
     * @param {string} phoneNumber - Número do destinatário
     * @param {string} mediaBase64 - Imagem em base64 (com prefixo data:image/...)
     * @param {string} caption - Legenda da imagem (texto)
     */
    sendEvolutionMedia: async (config, phoneNumber, mediaBase64, caption = "") => {
        if (PANEL_EVOLUTION_SEND_DISABLED) {
            console.warn("[IntegrationService] sendEvolutionMedia bloqueado:", PANEL_DISABLED_REASON);
            return { success: false, reason: PANEL_DISABLED_REASON, disabled: true };
        }
        const baseUrl = (config.apiUrl || "").replace(/\/$/, "");
        const instance = config.instanceName;

        let phoneNum = (phoneNumber || "").replace(/\D/g, "");
        if (phoneNum.startsWith("0")) phoneNum = phoneNum.substring(1);
        if (phoneNum && !phoneNum.startsWith("55")) phoneNum = "55" + phoneNum;

        if (!phoneNum || phoneNum.length < 10) {
            return { success: false, reason: "invalid_phone" };
        }

        // Extrair mimetype e base64 puro do data URI
        let cleanBase64 = mediaBase64;
        let mimetype = "image/jpeg";
        if (mediaBase64.startsWith("data:")) {
            const match = mediaBase64.match(/^data:(.*?);base64,(.*)$/);
            if (match) {
                mimetype = match[1];
                cleanBase64 = match[2];
            }
        }

        const msgRes = await fetch(`${baseUrl}/message/sendMedia/${instance}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", apikey: config.apiKey },
            body: JSON.stringify({
                number: phoneNum,
                mediatype: "image",
                mimetype: mimetype,
                media: cleanBase64,
                caption: caption || "",
                fileName: `image.${mimetype.split("/")[1] || "jpg"}`,
            }),
        });

        if (!msgRes.ok) {
            const msgData = await msgRes.json();
            return { success: false, reason: IntegrationService.extractErrorMessage(msgData, "Erro ao enviar mídia.") };
        }

        return { success: true };
    }
};

export default IntegrationService;
